<template>
  <div :class="['content', programStyle]" v-if="form">
    <div class="attention">Viewing Forms that Require Your Attention</div>
    <div class="flex-row form-list">
      <button :class="isFirstForm ? 'disabled' : ''" @click="goPrevious">Previous</button>
      <span class="title">Viewing Form {{ formListIndex + 1 }} of {{ formList.length }}</span>
      <button :class="isLastForm ? 'disabled' : ''" @click="goNext">Next</button>
    </div>
    <div class="appeal-container">
      <div class="title">{{ $t('appeal-review-tracking') }}</div>
      <div class="flex-column" v-if="selectedForm">
        <div class="flex-row form-info">
          <span>{{ $t('dealer-selectedform-dealername', [selectedForm.dealerName]) }}</span> |
          <span>{{ $t('dealer-code-selectedform-entity', [selectedForm.entity]) }}</span>
        </div>
        <div class="flex-row form-info">
          <span>{{ $t('bc-selectedform-bc', [selectedForm.bc]) }}</span> |
          <span>{{ $t('sales-district-form', [selectedForm.salesDistrict]) }}</span>
          |
          <span>{{ $t('service-district-form', [selectedForm.serviceDistrict]) }}</span>
        </div>
      </div>
      <FieldAppealDataTable :headings="tableHeadings" :rows="stepRows" :perPage="rowCount" :page="currentPage" />
    </div>
    <div class="form-container" v-if="appeals" id="dials">
      <div class="item-container" v-for="appeal in appeals" :key="appeal.dialId">
        <div class="left-container">
          <div class="dial-container" @click="selectDial(appeal)">
            <IconProgress
              v-if="appeal.dialTypeId === 'SMMT' || appeal.dialTypeId === 'FDMT'"
              :progress="appeal.progress"
              :targets="appeal.dialTargets"
              :color="appeal.dialColor"
            />
            <div class="smmtInner" v-if="appeal.dialResultText && appeal.progress">
              {{ appeal.dialResultText }}
            </div>
            <div
              @click="selectDial(appeal)"
              v-if="appeal.dialTypeId !== 'SMMT' && appeal.dialTypeId !== 'FDMT'"
              :class="['circle', appeal.dialColor]"
            >
              <div class="inner" v-if="appeal.dialResultText">
                {{ appeal.dialResultText }}
              </div>
            </div>
          </div>
          <Multiselect
            v-if="lineItemReviewer && (!appeal.statuses || editMode)"
            v-model="appeal.value"
            :options="
              appeal.dialTypeId === 'SMMT' || appeal.dialTypeId === 'FDMT'
                ? multiTargetOptions(appeal.dialTargets)
                : lineItemOptions
            "
            class="multiselect single"
            track-by="value"
            label="label"
            :placeholder="$t('no-action')"
            :allow-empty="false"
            :showLabels="false"
            :searchable="false"
            :class="{
              appealApproved:
                appeal.value.value === 'Approved' ||
                appeal.value.value === 'target-1' ||
                appeal.value.value === 'target-2' ||
                appeal.value.value === 'target-3',
              appealDeclined: appeal.value.value === 'Declined',
            }"
          ></Multiselect>
          <div v-if="appeal.statuses && !editMode">
            {{ appeal.statuses.result }}
          </div>
        </div>
        <div class="right-container">
          <div class="subtitle">{{ appeal.dialTitle }}</div>
          <div class="description">{{ $t('detailed-reason-for-appeal') }}</div>
          <textarea readonly v-model="appeal.reasonForAppeal" />
          <div>{{ $t('supporting-documents-0') }}</div>
          <div v-if="appeal.uploads">
            <div class="support-docs" v-for="upload in appeal.uploads" :key="upload.key">
              <span v-if="upload.label === appeal.dialTitle">{{ upload.originalFilename }}</span>
              <a class="relative" target="_blank" :href="upload.url">
                <img class="download-icon" src="@/assets/img/download-arrow.png" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="step-container">
        <div
          :class="['steps', status.id ? 'border-bottom' : '']"
          v-for="(status, index) in stepRows"
          :key="status.stepName"
        >
          <div class="bottom-info" v-if="(status.id || status.formId) && status.ordinal !== 0">
            <span class="info">{{ status.stepName }}</span>
            <div class="dial" v-if="!status.dialId && status.result !== 'Complete'">
              {{ $t('select-one') }}
              <div class="flex-row radio-container">
                <button :class="status.result === 'Approved' ? 'radio-selected' : 'radio'">
                  <div v-if="status.result === 'Approved'" class="selected"></div>
                </button>
                <span>{{ $t('approve-appeal') }}</span>
              </div>
              <div class="flex-row radio-container">
                <button :class="status.result === 'Declined' ? 'radio-selected' : 'radio'">
                  <div v-if="status.result === 'Declined'" class="selected"></div>
                </button>
                <span>{{ $t('deny-appeal') }}</span>
              </div>
            </div>
            <span class="label">{{ $t('reason-0') }}: </span>
            <textarea v-if="!editMode || stepRows.length - 1 !== index" readonly v-model="status.reason" />
            <GInput
              v-if="editMode && stepRows.length - 1 === index"
              type="textarea"
              v-model="reason"
              maxlength="1000"
              :showCount="true"
            ></GInput>
            <div class="flex-row input-info">
              <div class="flex-column">
                <span class="label">{{ $t('name') }}:</span>
                <input readonly v-model="status.name" />
              </div>
              <div class="flex-column">
                <span class="label">{{ $t('title') }}:</span>
                <input readonly v-model="status.title" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bottom-container">
        <div
          class="bottom-info"
          v-if="
            form.overallStatus !== 'completed' &&
            (form.permissions[0].canSubmit || form.permissions[0].canLineItemSubmit)
          "
        >
          <div class="dial" v-if="!lineItemReviewer">
            {{ $t('select-one') }}
            <div class="flex-row radio-container">
              <button :class="result === 'Approved' ? 'radio-selected' : 'radio'" @click="result = 'Approved'">
                <div v-if="result === 'Approved'" class="selected"></div>
              </button>
              <span>{{ $t('approve-appeal') }}</span>
            </div>
            <div class="flex-row radio-container">
              <button :class="result === 'Declined' ? 'radio-selected' : 'radio'" @click="result = 'Declined'">
                <div v-if="result === 'Declined'" class="selected"></div>
              </button>
              <span>{{ $t('deny-appeal') }}</span>
            </div>
          </div>
          <span class="label">{{ $t('reason-0') }}:</span>
          <GInput type="textarea" v-model="reason" maxlength="1000" :showCount="true"></GInput>
          <div v-if="comments.length > 0" class="comments-container flex-column">
            <span class="label">{{ $t('additional-comments') }}:</span>
            <div class="comments-row">
              <span class="comments" v-for="comment in comments" :key="comment"
                >{{ comment }}<span class="comment-comma">, </span></span
              >
            </div>
          </div>
          <div v-if="form.permissions[0].canAddNotes" class="notes-button-container flex-row">
            <button v-if="form.permissions[0].canAddNotes && !notes" class="small-btn" @click="isModalOpen = 'true'">
              {{ $t('add-notes') }}
            </button>
            <button v-if="form.permissions[0].canAddNotes && notes" @click="isModalOpen = 'true'">
              {{ $t('view-update-notes') }}
            </button>
          </div>
          <div class="flex-row input-info">
            <div class="flex-column">
              <span class="label">* {{ $t('name') }}:</span>
              <input readonly v-model="name" />
            </div>
            <div class="flex-column">
              <span class="label">* {{ $t('title') }}:</span>
              <input v-model="title" />
            </div>
            <div class="flex-column">
              <span class="label">Signature:</span>
              <VueSignaturePad id="signature" width="500px" height="100px" ref="signaturePad" :options="options" />
              <span class="signature-confirmation" v-if="signature">{{ $t('signature-saved') }}</span>
              <div class="signature-buttons flex-row">
                <span class="edit-button" @click="saveSignature">{{ $t('save') }}</span
                ><span class="separator">|</span
                ><span class="edit-button" @click="clearSignature">{{ $t('clear') }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="button-container">
          <button class="small-btn" @click="exit">{{ $t('exit') }}</button>
          <button
            v-if="form.permissions[0].canSubmit || form.permissions[0].canLineItemSubmit"
            value="save"
            @click="save"
            :class="[submitValidationCheck ? '' : 'disabled', 'small-btn']"
          >
            {{ $t('submit') }}
          </button>
          <button
            v-if="editMode"
            value="edit"
            @click="editFieldFormWithLineItems"
            :class="[lineItemEditValidationCheck ? '' : 'disabled', 'small-btn']"
          >
            {{ $t('submit') }}
          </button>
          <button class="small-btn" v-if="form.permissions[0].canEdit" @click="openEditModal">{{ $t('edit') }}</button>
          <button class="small-btn" v-if="form.permissions[0].canLineItemEdit && !editMode" @click="editLineItems">
            {{ $t('edit') }}
          </button>
          <div v-if="invalidPermissions">
            {{ $t('contact-admin') }}
          </div>
          <button class="small-btn" v-if="form.permissions[0].canReset" @click="isResetFormModalOpen = 'true'">
            {{ $t('reset-form') }}
          </button>
        </div>
      </div>
    </div>
    <Modal class="modal-screens" v-show="isModalOpen">
      <template v-slot:body class="body">
        <GInput type="textarea" v-model="notes" maxlength="1000" :showCount="true"></GInput>
        <div class="modal-button-container">
          <button class="submit" @click="saveNotes">{{ $t('save') }}</button>
          <button @click="cancelNotes">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
    <Modal class="modal-screens" v-show="isEditModalOpen">
      <template v-slot:body class="body">
        <div class="modal-container">
          <div class="modal-header">{{ selectedForm.mostRecentStepName }}</div>
          <div class="bottom-info">
            <div class="dial" v-if="!lineItemReviewer">
              {{ $t('select-one') }}
              <div class="flex-row radio-container">
                <button :class="result === 'Approved' ? 'radio-selected' : 'radio'" @click="result = 'Approved'">
                  <div v-if="result === 'Approved'" class="selected"></div>
                </button>
                <span>{{ $t('approve-appeal') }}</span>
              </div>
              <div class="flex-row radio-container">
                <button :class="result === 'Declined' ? 'radio-selected' : 'radio'" @click="result = 'Declined'">
                  <div v-if="result === 'Declined'" class="selected"></div>
                </button>
                <span>{{ $t('deny-appeal') }}</span>
              </div>
            </div>
            <span class="label">{{ $t('reason-0') }}:</span>
            <GInput type="textarea" v-model="reason" maxlength="1000" :showCount="true"></GInput>
          </div>
        </div>
        <div class="modal-button-container">
          <button :class="[simpleEditValidationCheck ? '' : 'disabled', 'submit']" @click="editFieldForm">
            {{ $t('save') }}
          </button>
          <button @click="closeEditModal">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
    <Modal class="modal-screens" v-show="isResetFormModalOpen">
      <template v-slot:body class="body">
        {{ $t('reset-form-warning') }}
        <div class="reset-modal-button-container">
          <button @click="resetForm">{{ $t('reset') }}</button>
          <button @click="isResetFormModalOpen = false">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
    <Modal class="modal-screens" v-show="isDialModalOpen">
      <template v-slot:body class="body" v-if="dialInfo">
        <div class="close" @click="isDialModalOpen = false"><Close /></div>
        <DialInfo :dialInfo="dialInfo" :period="selectedForm.config.period" />
      </template>
    </Modal>
  </div>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Multiselect from 'vue-multiselect';
import FieldAppealDataTable from '@/components/FieldAppealDataTable.vue';
import IconProgress from '@/components/IconProgress.vue';
import GInput from '@/components/GComponents/GInput.vue';
import Modal from '@/components/Modal.vue';
import DialInfo from '@/components/DialInfo.vue';
import Close from '@/assets/icons/x_icon_round.svg';
import { mapState } from 'vuex';
import fieldReviewTH from '@/utils/tableHeaders/fieldReviewTH';
import fieldReviewFr from '@/utils/tableHeaders/fieldReviewFr';

export default {
  name: 'FieldAppealMultiple',
  components: { FieldAppealDataTable, Multiselect, IconProgress, GInput, Modal, DialInfo, Close },
  props: {
    formList: {
      type: Array,
    },
  },
  data() {
    return {
      value: 'appeals',
      date: new Date(),
      saveClicked: false,
      form: null,
      rowCount: 7,
      currentPage: 1,
      stepRows: [],
      formInfo: null,
      appeals: [],
      reason: null,
      result: null,
      title: null,
      options: {
        penColor: '#000000',
      },
      signature: null,
      invalidPermissions: false,
      lineItemOptions: [
        { label: this.$t('approve'), value: 'Approved' },
        { label: this.$t('deny'), value: 'Declined' },
        { label: this.$t('no-action'), value: 'no-action' },
      ],
      filterValue: null,
      notes: null,
      isModalOpen: false,
      isDialModalOpen: false,
      editMode: false,
      isEditModalOpen: false,
      isResetFormModalOpen: false,
      fieldReviewTH: fieldReviewTH(),
      fieldReviewFr: fieldReviewFr(),
      statusRows: [],
      dialInfo: null,
      selectedForm: null,
      formId: this.formList[0],
      loader: this.$loading.show(),
      comments: [],
    };
  },
  methods: {
    getComments() {
      const loader = this.$loading.show();

      const { formId } = this.selectedForm;

      this.$store
        .dispatch('getComments', { formId })
        .then((res) => {
          if (res) {
            this.comments = res.formComments;
          }
        })
        .catch((error) => {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: error.data.error,
          });
        })
        .finally(() => {
          loader.hide();
        });
    },
    getFieldFormsWithId() {
      const { domain } = this;
      const formIdFilter = this.formId;
      this.$store
        .dispatch('getFieldFormById', {
          domain,
          formIdFilter,
        })
        .then((res) => {
          this.selectedForm = res.forms[0];
          this.$emit('updateSelectedForm', this.selectedForm);
          this.getForm();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        });
    },
    getForm() {
      const { formId } = this.selectedForm;

      this.stepRows = [];
      this.statusRows = [];
      this.appeals = [];

      this.notes = this.selectedForm.notes;

      this.$store
        .dispatch('getForm', { formId })
        .then((res) => {
          if (res) {
            this.form = res;
            this.createFormStepsAndStatus();
            this.setUpLineItemReview();
            this.getComments();
          }
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          this.loader.hide();
        });
    },
    save() {
      if (this.submitValidationCheck) {
        const { result, reason, title } = this;
        const items = [];

        if (this.lineItemReviewer) {
          this.appeals.forEach((appeal) => {
            if (appeal.value) {
              items.push({
                dialId: appeal.dialId,
                result: appeal.value.value,
              });
            } else {
              items.push({
                dialId: appeal.dialIId,
                result: 'Appeal Board',
              });
            }
          });
          this.result = 'Partial';
        }

        const params = {
          formId: this.selectedForm.formId,
          result,
          fieldSignature: this.signature,
          reason,
          title,
          items,
        };
        this.submitFieldForm(params);
      }
    },
    submitFieldForm(params) {
      const loader = this.$loading.show();

      this.$store
        .dispatch('updateForm', params)
        .then(() => {
          this.goNext();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    editFieldForm() {
      if (this.simpleEditValidationCheck) {
        const loader = this.$loading.show();
        loader.hide();

        const params = {
          formId: this.selectedForm.formId,
          stepId: this.mostRecentStatus.stepName,
          statuses: [
            {
              result: this.result,
              dialId: null,
              reason: this.reason,
            },
          ],
          items: [],
        };

        this.$store
          .dispatch('editForm', params)
          .then(() => {
            this.$notify({
              group: 'primary',
              type: 'success',
              text: this.$t('successful-edit'),
            });
            this.isEditModalOpen = false;
            this.getForm();
          })
          .catch((error) => {
            if (error.data) {
              this.$notify({
                group: 'primary',
                type: 'error',
                text: error.data.error,
              });
            }
          })
          .finally(() => {
            loader.hide();
          });
      }
    },
    editFieldFormWithLineItems() {
      if (this.lineItemEditValidationCheck) {
        const loader = this.$loading.show();

        const statuses = [];
        this.appeals.forEach((appeal) => {
          if (appeal.value) {
            statuses.push({
              dialId: appeal.dialId,
              result: appeal.value.value,
            });
          }
        });

        const params = {
          formId: this.selectedForm.formId,
          stepId: this.mostRecentStatus.stepName,
          statuses,
          items: [],
        };

        this.$store
          .dispatch('editForm', params)
          .then(() => {
            this.$notify({
              group: 'primary',
              type: 'success',
              text: this.$t('successful-edit'),
            });
            this.editMode = false;
            this.getForm();
          })
          .catch((error) => {
            if (error.data) {
              this.$notify({
                group: 'primary',
                type: 'error',
                text: error.data.error,
              });
            }
          })
          .finally(() => {
            loader.hide();
          });
      }
    },
    editLineItems() {
      this.editMode = true;
      document.getElementById('dials').scrollIntoView();
    },
    selectDial(dial) {
      const { entity } = this.selectedForm;
      const { period } = this.selectedForm.config;
      const domain = this.$store.state.global.programName;
      const dialIdObj = dial.dialId;
      const dialId = dialIdObj;

      if (dial.dialColor === 'FI' || dial.dialColor === 'X' || dial.dialTypeId === 'SNM' || !dial.dialColor) {
        return;
      }
      const loader = this.$loading.show();
      this.$store.dispatch('getDial', { period, entity, dialId, domain }).then((res) => {
        this.isDialModalOpen = true;
        this.dialInfo = res[0];
        loader.hide();
      });
    },
    exit() {
      this.$emit('exit');
    },
    saveSignature() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      if (!isEmpty) {
        this.signature = data;
      }
    },
    clearSignature() {
      this.$refs.signaturePad.undoSignature();
      this.signature = null;
    },
    saveNotes() {
      const { notes } = this;

      const params = {
        formId: this.selectedForm.formId,
        notes,
      };
      this.$store.dispatch('setNotes', params).then(() => {
        this.isModalOpen = false;
        this.selectedForm.notes = notes;
      });
    },
    cancelNotes() {
      if (this.selectedForm.notes) {
        this.notes = this.selectedForm.notes;
      } else {
        this.notes = null;
      }
      this.isModalOpen = false;
    },
    isEmpty(obj) {
      if (obj) {
        return Object.keys(obj).length === 0;
      }
      return false;
    },
    createFormStepsAndStatus() {
      this.form.steps.forEach((step, index) => {
        if (step.status && !this.isEmpty(step.status)) {
          this.$set(step.status, 'stepName', step.stepName);
          this.$set(step.status, 'ordinal', step.ordinal);
          if (this.form.header[0].isFormFinalized) {
            step.result = 'Completed';
          }
          this.stepRows.push(step.status);
          this.statusRows.push(step.status);
        } else if (
          index > 0 &&
          this.form.steps[index - 1].status &&
          !this.isEmpty(this.form.steps[index - 1].status) &&
          this.form.steps[index - 1].status.result !== 'Declined' &&
          this.form.steps[index - 1].status.result !== 'Pending'
        ) {
          this.stepRows.push({
            stepName: step.stepName,
            result: 'Pending',
            ordinal: step.ordinal,
          });
        } else {
          this.stepRows.push({
            stepName: step.stepName,
            result: '',
            ordinal: step.ordinal,
          });
        }
      });
    },
    setUpLineItemReview() {
      if (this.lineItemReviewer) {
        this.result = 'true';
        this.form.items.forEach((item) => {
          this.$set(item, 'value', {
            label: this.$t('no-action'),
            value: 'no-action',
          });
          this.appeals.push(item);
        });
      } else {
        this.form.items.forEach((item) => {
          this.appeals.push(item);
        });
      }
    },
    openEditModal() {
      this.isEditModalOpen = true;
    },
    closeEditModal() {
      this.reason = null;
      this.result = null;
      this.isEditModalOpen = false;
    },
    multiTargetOptions(targets) {
      const array = [
        { label: this.$t('deny'), value: 'Declined' },
        { label: this.$t('no-action'), value: 'no-action' },
      ];

      for (let i = 1; i <= targets; i += 1) {
        const obj = {
          label: this.$t('approve-to-target') + i,
          value: `target-${i}`,
        };
        array.push(obj);
      }

      return array;
    },
    resetForm() {
      const loader = this.$loading.show();
      const { formId } = this.selectedForm;
      this.$store
        .dispatch('resetForm', { formId })
        .then(() => {
          loader.hide();
          this.isResetFormModalOpen = false;
          this.getForm();
          this.$notify({
            group: 'primary',
            type: 'success',
            text: this.$t('form-reset-successful'),
          });
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        });
    },
    goNext() {
      if (!this.isLastForm) {
        this.formId = this.formList[this.formListIndex + 1];
      }
    },
    goPrevious() {
      if (!this.isFirstForm) {
        this.formId = this.formList[this.formListIndex - 1];
      }
    },
  },
  computed: {
    ...mapState('global', ['programStyle']),
    lineItemReviewer() {
      if (this.form.permissions[0].canLineItemSubmit || this.form.permissions[0].canLineItemEdit) {
        return true;
      }
      return false;
    },
    domain() {
      return this.$store.state.global.programName;
    },
    name() {
      const user = JSON.parse(localStorage.getItem('stellantis-user'));
      const fullName = `${user.firstName} ${user.lastName}`;

      return fullName;
    },
    fullValidationCheck() {
      if (this.title && this.reason && this.signature && this.result) {
        return true;
      }
      return false;
    },
    simpleEditValidationCheck() {
      if (this.reason && this.result) {
        return true;
      }
      return false;
    },
    lineItemEditValidationCheck() {
      if (this.allItemsSelected) {
        return true;
      }
      return false;
    },
    allItemsSelected() {
      let check = true;
      if (this.lineItemReviewer) {
        this.appeals.forEach((appeal) => {
          if (appeal.value.value === 'no-action') {
            check = false;
            return check;
          }
          return check;
        });
      }
      return check;
    },
    submitValidationCheck() {
      if (this.fullValidationCheck && this.allItemsSelected) {
        return true;
      }
      return false;
    },
    // Returns the most recent status that has an ID verifying that one has been submitted
    mostRecentStatus() {
      if (this.statusRows) {
        return this.statusRows.findLast((status) => status.id);
      }
      return null;
    },
    formListIndex() {
      return this.formList.findIndex((item) => item === this.formId);
    },
    isFirstForm() {
      if (this.formListIndex === 0) {
        return true;
      }
      return false;
    },
    isLastForm() {
      if (this.formListIndex === this.formList.length - 1) {
        return true;
      }
      return false;
    },
    selectedLanguage() {
      return this.$store.state.languages.selectedLanguage.code;
    },
    tableHeadings() {
      return this.selectedLanguage === 'en' ? this.fieldReviewTH : this.fieldReviewFr;
    },
    checkboxLabel() {
      return this.$t('not-appealing');
    },
    checkboxReason() {
      return this.$t('not-appealing-dial');
    },
  },
  watch: {
    formId() {
      this.form = null;
      this.reason = null;
      this.result = null;
      this.title = null;
      this.signature = null;
      this.loader = this.$loading.show();
      this.getFieldFormsWithId();
    },
  },
  created() {
    this.getFieldFormsWithId();
  },
};
</script>

<style lang="scss" scoped>
.content {
  color: #000000;
  text-align: left;
  .link {
    color: #243882;
  }
}
.upper {
  padding: 40px;
  display: flex;
  flex-direction: column;
  .flexRow {
    display: flex;
    button {
      margin-left: 20px;
      width: 180px;
    }
  }
}
.title-container {
  margin-left: 30px;
  margin-top: 30px;
}
.title {
  margin-bottom: 24px;
  color: #243882;
  font-size: 21px;
}
.item-container {
  display: flex;
  margin: 0 30px 20px 30px;
}
.right-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: 10px;
  font-size: 14px;
  input {
    height: 100px;
    width: initial;
    background: $input-background;
    border: none;
    margin-bottom: 20px;
    &:read-only {
      background: $input-readonly;
    }
  }
  .support-docs {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
  }
}
.left-container {
  display: flex;
  flex-direction: column;
}
.dial-container {
  text-align: center;
  margin-bottom: 10px;
  width: 80px;
  cursor: pointer;
}
.subtitle {
  font-size: 18px;
  margin-bottom: 30px;
}
.description {
  margin-bottom: 10px;
}
.bottom-container {
  margin-left: 30px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
}
.steps {
  margin-left: 30px;
  display: flex;
  flex-direction: column;
}
.step-container {
  display: flex;
  flex-direction: column;
}
.bottom-info {
  display: flex;
  flex-direction: column;
  margin-right: 30px;
  .info {
    margin-bottom: 15px;
  }
  input {
    width: initial;
    background: $input-background;
    border: 1px solid $light-grey;
    margin-bottom: 20px;
    margin-right: 30px;
    &:read-only {
      cursor: default;
      background: $input-readonly;
      border: none;
    }
    width: 250px;
  }
  .dial {
    margin-bottom: 40px;
    display: flex;
    align-items: center;
  }
  .label {
    margin-bottom: 20px;
  }
  .comments-container {
    margin: 34px 0px;
    .comments {
      font-size: 14px;
    }
    .comments:last-of-type {
      .comment-comma {
        visibility: hidden;
      }
    }
  }
  .notes-button-container {
    justify-content: flex-start;
    margin-bottom: 34px;
  }
}
.button-container {
  display: flex;
  margin-top: 40px;
  align-items: center;
  justify-content: center;
  button {
    margin-right: 30px;
  }
}
.date {
  width: 160px;
  background: $input-background;
  border: none;
  margin-bottom: 20px;
  margin-right: 20px;
  padding: 13px 0px 13px 20px;
}

#signature {
  background: $input-background;
  border: 1px solid $light-grey;
}
.signature-confirmation {
  font-size: 12px;
}
.border-bottom {
  margin-bottom: 30px;
}
.border-bottom:last-child {
  border: none !important;
}
.attention {
  background-color: $fca-gray;
  color: $primary-white;
  text-align: center;
  padding: 5px 0;
}
.appeal-container {
  margin: 40px 30px 20px 30px;
  .form-info {
    margin-bottom: 20px;
    span:not(:first-child) {
      margin: 0 15px;
    }
    span:first-child {
      margin-right: 15px;
    }
  }
}
.modal-container {
  .bottom-container {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
  }
}
.modal-header {
  margin-bottom: 20px;
}
.modal-button-container {
  display: flex;
  margin-top: 10px;
  .submit {
    margin-right: 20px;
  }
}
.reset-modal-button-container {
  display: flex;
  justify-content: center;
  button {
    margin: 20px 10px 0px;
  }
}
.smmtInner {
  left: -7px;
  position: absolute;
}
.modal-body {
  display: flex;
  flex-direction: column;
  .close {
    display: flex;
    justify-content: flex-end;
    svg {
      height: 30px;
      width: 30px;
      cursor: pointer;
    }
  }
}
.form-list {
  justify-content: space-between;
  align-items: center;
  margin: 20px 20px 0 20px;
  .title {
    margin: 0px;
  }
}
.signature-buttons {
  margin-top: 20px;
  justify-content: flex-end;
  .edit-button {
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
    color: #243882;
    cursor: pointer;
  }
  .separator {
    font-weight: lighter;
    color: $fca-gray;
    margin: 0px 9.5px;
  }
  .disabled {
    opacity: 0.7;
    cursor: default;
    pointer-events: none;
  }
}
</style>
